import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * HTML sanitisation for anything rendered through dangerouslySetInnerHTML.
 *
 * The backend's xss() filter is effective against raw HTML, but it cannot help
 * here: markdown link syntax contains no HTML, so `[x](javascript:alert(1))`
 * passes through storage byte-for-byte and only becomes an anchor when marked
 * renders it in the browser. Sanitising at render is therefore the only place
 * that closes it — and it also covers content already stored before the fix.
 *
 * Verified dangerous through marked 14 without this: javascript: and vbscript:
 * hrefs (including mixed-case), data:text/html, javascript: image sources, and
 * raw <img onerror> / <a href="javascript:"> passed straight through.
 */

// Tags we allow in user-authored content. Deliberately excludes script, style,
// iframe, object, embed, form and friends.
const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
];

const ALLOWED_ATTR = [
  'href', 'title', 'target', 'rel',
  'src', 'alt', 'width', 'height',
  'class', 'id',
  'colspan', 'rowspan',
  // Mentions and similar markers rely on data attributes.
  'data-mention', 'data-user-id', 'data-id', 'data-type',
];

const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  // Only these URL schemes may appear in href/src. This is what actually stops
  // javascript:, vbscript: and data:text/html.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  // Never allow inline event handlers, even on permitted tags.
  FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'base'],
  // Keep the text of removed elements rather than silently dropping content.
  KEEP_CONTENT: true,
};

// Any link we render points somewhere we do not control, so make sure it
// cannot reach back through window.opener.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('rel', 'noopener noreferrer nofollow');
    if (node.getAttribute('target') === '_blank') return;
    node.setAttribute('target', '_blank');
  }
});

/**
 * Sanitise a fragment of HTML. Use for content that is already HTML
 * (e.g. produced by the Lexical editor).
 */
export const sanitizeUserHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), PURIFY_CONFIG);
};

/**
 * Render markdown and sanitise the result. Use everywhere marked() output was
 * previously passed straight to dangerouslySetInnerHTML.
 */
export const renderMarkdown = (text) => {
  if (!text) return '';
  return sanitizeUserHtml(marked.parse(String(text)));
};

/**
 * Render a value that may be either stored HTML or markdown — the pattern used
 * for comments, which can arrive from the Lexical editor (HTML) or from the
 * API/MCP (markdown). Either way the result is sanitised.
 */
export const renderRichText = (text) => {
  if (!text) return '';
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(text);
  return looksLikeHtml ? sanitizeUserHtml(text) : renderMarkdown(text);
};

export default sanitizeUserHtml;
